<template>
  <section class="checkout-payment-embed checkout-payment-embed--stripe">
    <div class="checkout-payment-embed__hero">
      <div>
        <span class="checkout-payment-embed__label">Pago con tarjeta</span>
        <h3 class="checkout-payment-embed__title">Ingresa los datos de tu tarjeta para cerrar esta orden.</h3>
        <p class="checkout-payment-embed__copy">Si tu banco pide validación adicional, la hacemos aquí mismo sin sacarte del checkout.</p>
      </div>

      <div class="checkout-payment-embed__chips">
        <span>Débito y crédito</span>
        <span>Confirmación integrada</span>
        <span>Validación 3DS</span>
      </div>
    </div>

    <div v-if="errorMessage" class="workspace-feedback workspace-feedback--error">{{ errorMessage }}</div>
    <div v-if="successMessage" class="workspace-feedback workspace-feedback--success">{{ successMessage }}</div>

    <div v-show="loading" class="checkout-payment-embed__loading">
      <span class="checkout-payment-embed__loading-ring"></span>
      <div>
        <strong>Preparando formulario seguro...</strong>
        <small>Estamos montando la tarjeta dentro de FOMO.</small>
      </div>
    </div>

    <div ref="paymentElement" class="checkout-payment-embed__mount"></div>

    <p class="checkout-payment-embed__hint">Total a confirmar: <strong>{{ formattedAmount }}</strong></p>

    <div class="workspace-actions workspace-actions--end checkout-payment-embed__actions">
      <button class="btn btn-primary liquid-page-button checkout-payment-embed__button" type="button" :disabled="!canSubmit" @click="confirmPayment">
        <span class="liquid-page-button__glow"></span>
        <span class="liquid-page-button__ring"></span>
        <span class="liquid-page-button__inner">
          <span class="liquid-page-button__copy">{{ confirming ? 'Confirmando...' : `Pagar ${formattedAmount}` }}</span>
          <span class="liquid-page-button__icon"><i class="fas fa-lock"></i></span>
        </span>
      </button>
    </div>
  </section>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js'
import { formatCurrencyAmount, normalizeCurrencyCode } from '../../common/currency.js'
import { STRIPE_VERIFY_MESSAGE, STRIPE_APPROVED_MESSAGE } from '@/common/stripeRecovery'

export default {
  name: 'StripePaymentElement',
  props: {
    attemptToken: {
      type: String,
      required: true,
    },
    clientSecret: {
      type: String,
      required: true,
    },
    publishableKey: {
      type: String,
      required: true,
    },
    customerName: {
      type: String,
      default: '',
    },
    customerEmail: {
      type: String,
      default: '',
    },
    customerPhone: {
      type: String,
      default: '',
    },
    defaultCountryCode: {
      type: String,
      default: '',
    },
    amount: {
      type: [Number, String],
      default: 0,
    },
    currency: {
      type: String,
      default: 'USD',
    },
  },
  data() {
    return {
      loading: false,
      confirming: false,
      ready: false,
      complete: false,
      stripe: null,
      elements: null,
      paymentElement: null,
      errorMessage: '',
      successMessage: '',
    }
  },
  computed: {
    formattedAmount() {
      return formatCurrencyAmount(this.amount, normalizeCurrencyCode(this.currency, 'USD'))
    },
    normalizedCustomerName() {
      return typeof this.customerName === 'string' ? this.customerName.trim() : ''
    },
    normalizedCustomerEmail() {
      return typeof this.customerEmail === 'string' ? this.customerEmail.trim() : ''
    },
    normalizedCustomerPhone() {
      return typeof this.customerPhone === 'string' ? this.customerPhone.trim() : ''
    },
    normalizedCountryCode() {
      return typeof this.defaultCountryCode === 'string' ? this.defaultCountryCode.trim().toUpperCase() : ''
    },
    defaultValues() {
      if (!this.normalizedCountryCode) {
        return undefined
      }

      return {
        billingDetails: {
          address: {
            country: this.normalizedCountryCode,
          },
        },
      }
    },
    billingFields() {
      return {
        name: this.normalizedCustomerName ? 'never' : 'auto',
        email: this.normalizedCustomerEmail ? 'never' : 'auto',
        phone: this.normalizedCustomerPhone ? 'never' : 'auto',
        address: 'auto',
      }
    },
    confirmBillingDetails() {
      const details = {}

      if (this.normalizedCustomerName) {
        details.name = this.normalizedCustomerName
      }
      if (this.normalizedCustomerEmail) {
        details.email = this.normalizedCustomerEmail
      }
      if (this.normalizedCustomerPhone) {
        details.phone = this.normalizedCustomerPhone
      }

      return details
    },
    canSubmit() {
      return !this.loading && !this.confirming && this.ready && this.complete
    },
  },
  mounted() {
    this.initialize()
  },
  beforeUnmount() {
    if (this.paymentElement) {
      this.paymentElement.unmount()
      this.paymentElement.destroy()
      this.paymentElement = null
    }
    this.elements = null
    this.stripe = null
  },
  methods: {
    async initialize() {
      this.loading = true
      this.ready = false
      this.complete = false
      this.errorMessage = ''
      this.successMessage = ''

      if (!this.publishableKey || !this.clientSecret) {
        this.loading = false
        this.errorMessage = 'Faltan credenciales públicas de Stripe o client secret.'
        return
      }

      this.stripe = await loadStripe(this.publishableKey)
      if (!this.stripe) {
        this.loading = false
        this.errorMessage = 'Stripe no pudo cargarse en este navegador.'
        return
      }

      const compactViewport = typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches

      this.elements = this.stripe.elements({
        clientSecret: this.clientSecret,
        locale: 'es',
        appearance: {
          theme: 'night',
          variables: {
            colorPrimary: '#cdbff0',
            colorBackground: '#0d1017',
            colorText: '#eef3ff',
            colorDanger: '#ff7b9c',
            colorTextSecondary: '#c8d1e4',
            colorTextPlaceholder: '#9aa5bc',
            borderRadius: compactViewport ? '14px' : '16px',
            spacingUnit: compactViewport ? '4px' : '5px',
            gridRowSpacing: compactViewport ? '12px' : '14px',
            gridColumnSpacing: compactViewport ? '12px' : '14px',
            fontSizeBase: '16px',
            fontFamily: 'Space Grotesk, sans-serif',
            iconColor: '#c8d1e4',
          },
          rules: {
            '.Block': {
              backgroundColor: 'rgba(7, 10, 17, 0.92)',
              boxShadow: 'none',
            },
            '.Input': {
              backgroundColor: 'rgba(255, 255, 255, 0.035)',
              border: '1px solid rgba(198, 205, 220, 0.12)',
              padding: compactViewport ? '15px 16px' : '16px 18px',
              boxShadow: 'none',
            },
            '.Input:focus': {
              border: '1px solid rgba(205, 191, 240, 0.28)',
              boxShadow: '0 0 0 1px rgba(205, 191, 240, 0.08)',
            },
            '.Label': {
              color: '#eef3ff',
              fontWeight: '600',
            },
            '.AccordionItem': {
              border: '1px solid rgba(198, 205, 220, 0.1)',
              backgroundColor: 'rgba(255, 255, 255, 0.025)',
              boxShadow: 'none',
            },
            '.Tab': {
              border: '1px solid rgba(198, 205, 220, 0.1)',
            },
          },
        },
      })

      this.paymentElement = this.elements.create('payment', {
        layout: {
          type: 'accordion',
          defaultCollapsed: false,
          radios: 'never',
          spacedAccordionItems: false,
        },
        paymentMethodOrder: ['card'],
        defaultValues: this.defaultValues,
        fields: {
          billingDetails: this.billingFields,
        },
        wallets: {
          applePay: 'never',
          googlePay: 'never',
          link: 'never',
        },
      })

      this.paymentElement.on('ready', () => {
        this.ready = true
        this.loading = false
      })

      this.paymentElement.on('change', (event) => {
        this.complete = !!event.complete
        if (event.error && event.error.message) {
          this.errorMessage = event.error.message
          return
        }
        this.errorMessage = ''
      })

      this.paymentElement.mount(this.$refs.paymentElement)
    },
    async confirmPayment() {
      if (!this.stripe || !this.elements || !this.canSubmit) {
        return
      }

      this.confirming = true
      this.errorMessage = ''
      this.successMessage = ''

      try {
        const confirmParams = {}
        if (Object.keys(this.confirmBillingDetails).length) {
          confirmParams.payment_method_data = {
            billing_details: this.confirmBillingDetails,
          }
        }

        const { error, paymentIntent } = await this.stripe.confirmPayment({
          elements: this.elements,
          confirmParams,
          redirect: 'if_required',
        })

        if (error) {
          this.errorMessage = error.type === 'validation_error'
            ? (error.message || 'Revisa los datos de tu tarjeta antes de continuar.')
            : STRIPE_VERIFY_MESSAGE
          if (error.type !== 'validation_error') {
            this.$emit('completed', { attemptToken: this.attemptToken,
              paymentIntentId: error.payment_intent?.id || '', status: error.payment_intent?.status || 'unknown' })
          }
          return
        }

        this.successMessage = paymentIntent?.status === 'succeeded' ? STRIPE_APPROVED_MESSAGE : STRIPE_VERIFY_MESSAGE
        this.$emit('completed', {
          attemptToken: this.attemptToken,
          paymentIntentId: paymentIntent && paymentIntent.id ? paymentIntent.id : '',
          status: paymentIntent && paymentIntent.status ? paymentIntent.status : 'processing',
        })
      } catch (_error) {
        this.errorMessage = STRIPE_VERIFY_MESSAGE
        this.$emit('completed', { attemptToken: this.attemptToken, status: 'unknown' })
      } finally {
        this.confirming = false
      }
    },
  },
}
</script>

<style scoped>
.checkout-payment-embed--stripe {
  display: grid;
  gap: 12px;
  width: 100%;
  padding: 0;
  box-sizing: border-box;
  border: 0;
  background: transparent;
  box-shadow: none;
}

.checkout-payment-embed__hero {
  display: grid;
  gap: 10px;
}

.checkout-payment-embed__label {
  color: #c8d1e4;
}

.checkout-payment-embed__title {
  margin: 6px 0 8px;
  color: #f5f7fb;
  font-size: clamp(1.2rem, 2vw, 1.6rem);
}

.checkout-payment-embed__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.checkout-payment-embed__chips span {
  display: inline-flex;
  align-items: center;
  min-height: 38px;
  padding: 0 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(198, 205, 220, 0.12);
  color: #eef3ff;
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.checkout-payment-embed__copy,
.checkout-payment-embed__loading {
  margin: 0;
  color: rgba(203, 210, 224, 0.8);
}

.checkout-payment-embed__loading {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(198, 205, 220, 0.09);
}

.checkout-payment-embed__loading strong,
.checkout-payment-embed__hint {
  color: #eef3ff;
}

.checkout-payment-embed__loading small {
  display: block;
  color: rgba(200, 209, 228, 0.72);
}

.checkout-payment-embed__loading-ring {
  width: 18px;
  height: 18px;
  border-radius: 999px;
  border: 2px solid rgba(255, 255, 255, 0.22);
  border-top-color: #eef3ff;
  animation: stripe-spin 0.9s linear infinite;
}

.checkout-payment-embed__mount {
  width: 100%;
  margin: 0;
  padding: 0;
  border-radius: 0;
  background: transparent;
  border: 0;
  box-shadow: none;
  box-sizing: border-box;
}

.checkout-payment-embed__hint {
  margin: -2px 0 0;
  font-size: 0.92rem;
  line-height: 1.5;
}

.checkout-payment-embed__actions {
  width: 100%;
  padding-top: 0;
}

.checkout-payment-embed__button {
  display: block;
  width: 100%;
  min-width: 0;
  min-height: 68px;
  border: 0;
}

.checkout-payment-embed__button .liquid-page-button__inner {
  justify-content: space-between;
  padding: 0 24px;
}

@keyframes stripe-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 640px) {
  .checkout-payment-embed--stripe {
    gap: 10px;
  }

  .checkout-payment-embed__hero {
    gap: 8px;
  }

  .checkout-payment-embed__title {
    margin: 4px 0 6px;
    font-size: 1.04rem;
    line-height: 1.2;
  }

  .checkout-payment-embed__copy {
    font-size: 0.86rem;
    line-height: 1.45;
  }

  .checkout-payment-embed__chips {
    gap: 8px;
  }

  .checkout-payment-embed__chips span {
    min-height: 32px;
    padding: 0 10px;
    font-size: 0.68rem;
    letter-spacing: 0.05em;
  }

  .checkout-payment-embed__loading {
    gap: 10px;
    padding: 8px 10px;
  }

  .checkout-payment-embed__mount {
    padding: 0;
    border-radius: 0;
  }

  .checkout-payment-embed__hint {
    font-size: 0.86rem;
  }

  .checkout-payment-embed__button {
    min-height: 62px;
  }

  .checkout-payment-embed__button .liquid-page-button__inner {
    padding: 0 18px;
  }
}
</style>
